import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {logUnreachableCode} from "@khan/logging";

import arrowsClockwise from "@phosphor-icons/core/regular/arrows-clockwise.svg";
import cloudCheck from "@phosphor-icons/core/regular/cloud-check.svg";
import cloudWarning from "@phosphor-icons/core/regular/cloud-warning.svg";

// The possible save statuses.
export type SavedStatus = "unsaved" | "saving" | "saved" | "error";
export interface Labels {
    error: string;
    saved: string;
    saving: string;
    ariaError: string;
    ariaSaved: string;
    ariaSaving: string;
}

interface Props {
    size: "small" | "medium";
    // The current status to display.
    status: SavedStatus;
    labels?: Labels;
}

/**
 * This component displays the current save status of something that is saved
 * to the server.
 * @param status The current status to display.
 */
export default function SaveIndicator({
    labels,
    size,
    status,
}: Props): React.ReactElement {
    const iconSize = size === "medium" ? "medium" : undefined;
    const strings = labels || {
        error: t`Error while saving`,
        saved: t`Saved`,
        saving: t`Saving...`,
        ariaError: t`There are unsaved changes`,
        ariaSaved: t`Changes saved`,
        ariaSaving: t`Saving changes`,
    };

    switch (status) {
        case "unsaved":
            return <View />;
        case "saving":
            return (
                <View style={styles.saveIndicator}>
                    <PhosphorIcon
                        aria-label={strings.ariaSaving}
                        icon={arrowsClockwise}
                        role="img"
                        size={iconSize}
                    />
                    <Body
                        aria-hidden="true"
                        style={size === "small" && styles.smallText}
                    >
                        {strings.saving}
                    </Body>
                </View>
            );
        case "saved":
            return (
                <View style={styles.saveIndicator}>
                    <PhosphorIcon
                        aria-label={strings.ariaSaved}
                        icon={cloudCheck}
                        role="img"
                        size={iconSize}
                    />
                    <Body
                        aria-hidden="true"
                        style={size === "small" && styles.smallText}
                    >
                        {strings.saved}
                    </Body>
                </View>
            );
        case "error":
            return (
                <View role="alert" style={[styles.saveIndicator, styles.error]}>
                    <PhosphorIcon
                        aria-label={strings.ariaError}
                        icon={cloudWarning}
                        role="img"
                        size={iconSize}
                    />
                    <Body
                        aria-hidden="true"
                        style={size === "small" && styles.smallText}
                    >
                        {strings.error}
                    </Body>
                </View>
            );
        default:
            throw logUnreachableCode("Unknown status", status);
    }
}

const styles = StyleSheet.create({
    saveIndicator: {
        alignItems: "center",
        color: color.offBlack64,
        display: "flex",
        flexDirection: "row",
        flexShrink: 0,
        gap: spacing.xSmall_8,
    },
    error: {
        color: color.red,
    },
    smallText: {
        fontSize: font.size.small,
    },
});
